import type { BaseHit, Hit } from 'instantsearch.js'
import type { HitsProps } from 'react-instantsearch'
import { l, t } from '../../../../assets/utils/algolia/algoliaLocalization'
import { formatEventDate, formatEventTime, getEventTimezoneLabel } from '../../../../assets/utils/format'
import { externalLinkProps } from '../../../../assets/frontend/js/utils/externalUrl'
import CardHitComponent from './CardHitComponent'
import { useConfigContext } from './ConfigContext'
import ListHitComponent from './ListHitComponent'

export type HitComponentProps = Omit<
  NonNullable<
    HitsProps<BaseHit>['hitComponent']
  > extends React.JSXElementConstructor<infer P>
    ? P
    : never,
  'sendEvent'
>

export type HitComponentTemplateProps = HitComponentProps & {
  isNew: boolean
  isGlobal: boolean
  isRegional: boolean
  regionalOrganisationName: string
  isPermanent: boolean
  isCancelled: boolean
  tags: string[]
  location: string
  date: string
  time: string
  detailUrl: string
  detailLinkProps: ReturnType<typeof externalLinkProps>
  ctaButtonText: string
  ctaButtonUrl: string
  ctaLinkProps: ReturnType<typeof externalLinkProps>
  teaser: React.ReactNode
}

// Fallback for hits without an excerpt: the description holds sanitized HTML,
// so strip the tags and shorten it to roughly the snippet length.
function plainDescriptionTeaser(hit: Hit<BaseHit>) {
  const container = document.createElement('div')
  // Tags become spaces first so block boundaries keep separating words; the DOM
  // round-trip then decodes the entities (&amp;, &nbsp;, …) left in the text.
  container.innerHTML = t(hit.description, { fallback: 'any' }).replace(
    /<[^>]*>/g,
    ' '
  )
  const text = (container.textContent ?? '').replace(/\s+/g, ' ').trim()
  const words = text.split(' ')
  return words.length > 20 ? `${words.slice(0, 20).join(' ')} …` : text
}

function isEventNew(item: Hit<BaseHit>) {
  const eventPublishTimestamp = item.publishAt
  const nowTimestamp = Date.now()
  const twoWeeksAgo = nowTimestamp - 14 * 24 * 60 * 60 * 1000
  return eventPublishTimestamp >= twoWeeksAgo
}

export default function HitComponent({
  hit,
  variant
}: HitComponentProps & { variant: 'card' | 'list' }) {
  const { baseUrl, baseUrls, siteUrl, organisationId } = useConfigContext()

  const isNew = isEventNew(hit)

  const hitOrganisationId = (hit.organization_id ?? hit.organizationId) as string | number | undefined
  const hitOrganisationSlug = (hit.organization as { slug?: string } | undefined)?.slug

  const regionalOrganisationName = t(
    (hit.organization as { name?: Record<string, unknown> } | undefined)?.name,
    { fallback: 'any' }
  )

  const isSameOrganisation =
    (!!organisationId && hitOrganisationId === organisationId) ||
    (!!organisationId && hitOrganisationSlug === organisationId)

  const isRegional =
    !hit.isDenominationGlobal &&
    (!!hitOrganisationId || !!hitOrganisationSlug) &&
    !isSameOrganisation &&
    !!regionalOrganisationName


  // Render times in the Hub-resolved event zone (wall-clock at the venue), with an
  // "Ortszeit …"-label when that differs from the site zone.
  const eventTimezone = (hit.timezone as string | undefined) || undefined

  const dateString = formatEventDate(hit.startDate, hit.endDate, eventTimezone)
  const rawTimeString = formatEventTime(
    hit.startDate,
    hit.endDate,
    (hit.no_start_time ?? hit.noStartTime) as boolean | undefined,
    (hit.no_end_time ?? hit.noEndTime) as boolean | undefined,
    eventTimezone
  )
  const timezoneLabel = rawTimeString
    ? getEventTimezoneLabel(eventTimezone, hit.startDate ? new Date(hit.startDate) : undefined)
    : ''
  const timeString = timezoneLabel ? `${rawTimeString} (${timezoneLabel})` : rawTimeString

  const isCancelled = !!hit.isCancelled

  const tags = Array.isArray(hit.tags)
    ? (hit.tags as ({ title: Record<string, string> } | undefined)[])
        .map((tag) => t(tag?.title, { fallback: 'none' }))
        .filter(Boolean)
    : []

  const placeName = t(hit.place?.name, { fallback: 'any' })
  const alternativePlace = t(hit.alternativePlace, { fallback: 'none' })
  const displayLocation = alternativePlace || placeName

  const ctaButtonText = t(hit.ctaButtonText, { fallback: 'any' })
  const ctaButtonLink = t(hit.ctaButtonUrl, { fallback: 'any' })

  const slugLocale = l(hit.slug as Record<string, unknown>, { fallback: 'any' })
  const slugLang = slugLocale.split('_')[0]
  const slugBaseUrl = baseUrls[slugLang] ?? baseUrl
  const rawDetailUrl =
    t(hit.detailsUrl, { fallback: 'any' }) || `${slugBaseUrl}${t(hit.slug, { fallback: 'any' })}`
  const detailUrl = rawDetailUrl.endsWith('/')
    ? rawDetailUrl
    : `${rawDetailUrl}/`

  const detailLinkPropsValue = externalLinkProps(detailUrl, siteUrl)
  const ctaLinkPropsValue = externalLinkProps(ctaButtonLink, siteUrl)

  // Hub-side limited to ~100 chars; older index objects fall back to the shortened description
  const teaser = t(hit.excerpt, { fallback: 'any' }) || plainDescriptionTeaser(hit)

  if (variant === 'list') {
    return (
      <ListHitComponent
        hit={hit}
        isNew={isNew}
        isGlobal={!!hit.isDenominationGlobal}
        isRegional={isRegional}
        regionalOrganisationName={regionalOrganisationName}
        isPermanent={!!hit.isPermanent}
        isCancelled={isCancelled}
        tags={tags}
        location={displayLocation}
        date={dateString ?? ''}
        time={timeString ?? ''}
        detailUrl={detailUrl}
        detailLinkProps={detailLinkPropsValue}
        ctaButtonText={ctaButtonText}
        ctaButtonUrl={ctaButtonLink}
        ctaLinkProps={ctaLinkPropsValue}
        teaser={teaser}
      />
    )
  }

  if (variant === 'card') {
    return (
      <CardHitComponent
        hit={hit}
        isNew={isNew}
        isGlobal={!!hit.isDenominationGlobal}
        isRegional={isRegional}
        regionalOrganisationName={regionalOrganisationName}
        isPermanent={!!hit.isPermanent}
        isCancelled={isCancelled}
        tags={tags}
        location={displayLocation}
        date={dateString ?? ''}
        time={timeString ?? ''}
        detailUrl={detailUrl}
        detailLinkProps={detailLinkPropsValue}
        ctaButtonText={ctaButtonText}
        ctaButtonUrl={ctaButtonLink}
        ctaLinkProps={ctaLinkPropsValue}
        teaser={teaser}
      />
    )
  }

  return null
}
